import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import {
  Form,
  ButtonsWrapper,
  Button,
  TextInput,
  SelectInput,
  Typography,
  TYPOGRAPHY_SIZE,
  ICONS,
  PLATFORM_THEME_COLORS,
  DeleteModal,
} from '@thinknet-gmbh/platform-common-ui';

import { useGetAllEmailTemplate } from 'src/hooks/email-templates/get-all-platform-email-template.hook';

import {
  ProcessTemplateWrapper,
  FormBodyContainer,
  HeaderFormContainer,
  FieldContainer,
} from './process-template-form.styles';

import { ProcessTasksTable } from 'src/components/process-tasks-table';
import { DrawerComponent } from 'src/components/custom-drawer';
import { ChangeTemplateTask } from './components/change-template-task';

import { PROCESS_TASKS_MODALS } from '../../../../process-templates.constants';

import {
  IProcessesTemplateItem,
  IProcessTemplateItemTask,
} from '../../../process-templates-table/process-templates-table.interfaces';

const ProcessTemplateForm = ({
  onFinishHandler,
  processTemplate,
  tasks,
}: {
  onFinishHandler: (
    values: IProcessesTemplateItem,
    taskList: IProcessTemplateItemTask[],
    taskIdsToDelete?: string[]
  ) => void;
  processTemplate?: IProcessesTemplateItem;
  tasks?: IProcessTemplateItemTask[];
}) => {
  const { t } = useTranslation('settings');

  const [taskList, setTaskList] = useState<IProcessTemplateItemTask[]>(tasks || []);
  const [selectedTask, setSelectedTask] = useState<IProcessTemplateItemTask | null>(null);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [taskIdsToDelete, setTaskIdsToDelete] = useState<string[]>([]);

  const { templates = [], loading } = useGetAllEmailTemplate();

  const { name, emailTemplateId } = processTemplate || {};

  const closeModal = () => {
    setActiveModal(null);
    setSelectedTask(null);
  };

  const onCreateTask = async (task: IProcessTemplateItemTask) => {
    setTaskList(prev => [{ key: uuidv4(), ...task }, ...prev]);
    closeModal();
  };

  const onUpdateTask = (task: IProcessTemplateItemTask) => {
    const editedTaskIndex = taskList.findIndex(el => el?.key === selectedTask?.key);
    const editedTask = taskList[editedTaskIndex];
    const newList = [...taskList];
    newList.splice(editedTaskIndex, 1, {
      ...task,
      ...(editedTask?._id && { _id: editedTask._id }),
    });
    setTaskList(newList);
    closeModal();
  };

  const handleTaskModalOpen = (type: string) => () => {
    setActiveModal(type);
  };

  const deleteTask = () => {
    setSelectedTask(null);
    closeModal();
  };

  const onSubmitDelete = () => {
    if (selectedTask?._id) {
      const newIds = [...taskIdsToDelete, selectedTask._id];
      setTaskIdsToDelete(newIds);
    }
    const newList = taskList.filter(task => task?.key !== selectedTask?.key);
    setTaskList(newList);
    deleteTask();
  };

  const isCreateTask = activeModal === PROCESS_TASKS_MODALS.create;
  const isEditTask = activeModal === PROCESS_TASKS_MODALS.edit;

  return (
    <ProcessTemplateWrapper>
      <DrawerComponent
        title={t(`processBuilder:tasks.labels.${isCreateTask ? 'addNewTask' : 'editTask'}`)}
        onClose={closeModal}
        visible={isEditTask || isCreateTask}
        destroyOnClose
      >
        <ChangeTemplateTask
          onClose={closeModal}
          task={selectedTask}
          onCreateTask={onCreateTask}
          onUpdateTask={onUpdateTask}
        />
      </DrawerComponent>
      <DeleteModal
        translate={t}
        isOpened={activeModal === PROCESS_TASKS_MODALS.delete}
        title={t('processBuilder:tasks.deleteModal.title')}
        subTitle={t('processBuilder:tasks.deleteModal.subtitle', {
          taskName: selectedTask?.title,
        })}
        onClose={closeModal}
        onSubmit={onSubmitDelete}
      />
      <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
        {t('processTemplates.labels.addNewProcessTemplate')}
      </Typography>
      <Form
        layout="vertical"
        onFinish={values => onFinishHandler(values, taskList, taskIdsToDelete)}
        initialValues={{
          name: name || '',
          emailTemplateId: emailTemplateId || '',
        }}
      >
        <FormBodyContainer>
          <HeaderFormContainer>
            <FieldContainer>
              <Form.Item
                name="name"
                label={t('processTemplates.labels.title')}
                rules={[
                  { required: true, message: t('settings:employeeQuestionnaire.validation.name') },
                ]}
              >
                <TextInput placeholder={t('processTemplates.labels.enterTitle')} />
              </Form.Item>

              {!loading && (
                <Form.Item
                  name="emailTemplateId"
                  label={t('processTemplates.labels.selectReminderTemplate')}
                  rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
                >
                  <SelectInput placeholder={t('processTemplates.labels.selectReminderTemplate')}>
                    {templates?.map(({ _id, subject }: { _id: string; subject: string }) => (
                      <SelectInput.Option key={_id} value={_id}>
                        {subject}
                      </SelectInput.Option>
                    ))}
                  </SelectInput>
                </Form.Item>
              )}
            </FieldContainer>

            <ButtonsWrapper marginTop={30} className={'process-template-buttons'}>
              <Button
                type="primary"
                ghost
                icon={ICONS.plus}
                iconColor={PLATFORM_THEME_COLORS.primary.blue}
                onClick={handleTaskModalOpen(PROCESS_TASKS_MODALS.create)}
              >
                {t('processTemplates.buttons.addTask')}
              </Button>
              <Button htmlType="submit" type="primary">
                {t('common:buttons.save')}
              </Button>
            </ButtonsWrapper>
          </HeaderFormContainer>
          <ProcessTasksTable
            taskList={taskList}
            setTaskList={setTaskList}
            handleModals={(modal, record) => {
              setActiveModal(modal);
              setSelectedTask(record);
            }}
            emptyTitle={t('settings:processTemplates.labels.noData')}
          />
        </FormBodyContainer>
      </Form>
    </ProcessTemplateWrapper>
  );
};

export default ProcessTemplateForm;
